/**
 * Admin role permission map.
 *
 * Maps sidebar items to the admin roles allowed to see them.
 * An empty array means ALL admin roles can see the item.
 */

export const ADMIN_ROLES = [
    { id: 'SUPER_ADMIN', name: 'Super Admin' },
    { id: 'OPERATIONS', name: 'Operations' },
    { id: 'SUPPORT', name: 'Support' },
    { id: 'TRUST_SAFETY', name: 'Trust & Safety' },
    { id: 'FINANCE', name: 'Finance' },
    { id: 'VERIFICATION', name: 'Verification' },
    { id: 'CONTENT', name: 'Content' },
];

/**
 * Permission map: resource/route key → array of roles that can access it.
 * Empty array = all admin roles.
 */
export const PERMISSION_MAP = {
    // Dashboard — everyone
    dashboard: [],

    // Resources
    users: ['OPERATIONS', 'SUPER_ADMIN'],
    requests: ['OPERATIONS', 'SUPER_ADMIN'],
    userservices: ['OPERATIONS', 'SUPER_ADMIN'],
    defaultservices: ['OPERATIONS', 'SUPER_ADMIN'],
    feeds: ['OPERATIONS', 'SUPER_ADMIN'],
    logs: ['SUPER_ADMIN'],
    /*
     * SUPER_ADMIN only, matching `logs`. The audit trail records what every
     * other admin did, including grants of paid plans -- the people it holds
     * accountable must not be the people who can read (or be seen reading) it.
     * Deliberately NOT given to OPERATIONS.
     */
    'audit-logs': ['SUPER_ADMIN'],
    tickets: ['SUPPORT', 'TRUST_SAFETY', 'SUPER_ADMIN'],
    financials: ['FINANCE', 'SUPER_ADMIN'],
    pricing: ['FINANCE', 'SUPER_ADMIN'],
    'test-credits': ['FINANCE', 'SUPER_ADMIN'],

    // Custom routes
    disputes: ['TRUST_SAFETY', 'SUPER_ADMIN'],
    cancellations: ['TRUST_SAFETY', 'SUPER_ADMIN'],
    'page-analytics': ['OPERATIONS', 'SUPER_ADMIN'],
    'user-journey': ['OPERATIONS', 'SUPER_ADMIN'],
    'recently-active-users': ['OPERATIONS', 'SUPER_ADMIN'],
    'guest-visitors': ['OPERATIONS', 'SUPER_ADMIN'],
    // Meta kill switches: OPERATIONS reads (GET /admin/meta-flags); only
    // SUPER_ADMIN can toggle (PATCH), enforced in the page and on the backend.
    'meta-flags': ['OPERATIONS', 'SUPER_ADMIN'],
    // Backend kill switches: SUPER_ADMIN only for BOTH read (GET
    // /admin/feature-flags) and toggle (PATCH), mirroring skilzBackend
    // requireAdminRoles(['SUPER_ADMIN']). Unlike meta-flags, OPERATIONS cannot read.
    'feature-flags': ['SUPER_ADMIN'],
    debt: ['FINANCE', 'SUPER_ADMIN'],
    'verification-queue': ['VERIFICATION', 'SUPER_ADMIN'],
    reports: ['OPERATIONS', 'SUPER_ADMIN'],
    // Mirrors skilzBackend requireAdminRoles(['SUPPORT','FINANCE','OPERATIONS']) on
    // /admin/referrals/* exactly; SUPER_ADMIN passes via hasAccess's short-circuit.
    'referral-rewards': ['SUPPORT', 'FINANCE', 'OPERATIONS'],
    'contact-moderation': ['TRUST_SAFETY', 'SUPER_ADMIN'],
    'credentials-review': ['VERIFICATION', 'SUPER_ADMIN'],
    communities: ['OPERATIONS'],

    // Actions
    'unlock-legal-name': ['SUPER_ADMIN'],
    translations: ['OPERATIONS', 'CONTENT', 'SUPER_ADMIN'],
};

/**
 * Check whether a user with `userRoles` can access the given resource/route.
 *
 * @param {string} resourceKey - key from PERMISSION_MAP
 * @param {string[]} userRoles - the user's adminRoles array
 * @returns {boolean}
 */
export const hasAccess = (resourceKey, userRoles = []) => {
    // SUPER_ADMIN bypasses all checks
    if (userRoles.includes('SUPER_ADMIN')) return true;

    const allowed = PERMISSION_MAP[resourceKey];
    // If not in the map or empty array → everyone can see it
    if (!allowed || allowed.length === 0) return true;

    return allowed.some(role => userRoles.includes(role));
};
